import React, { useEffect } from "react";
import {
  Container,
  Row,
  Card,
  CardBody,
  Col,
  Label,
  Input,
  FormFeedback,
  Alert,
  Spinner,
  Form,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import { sendNotificationV2 } from "../../store/notification/actions";

const NotifyUsersPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  document.title = `${t("Notifications.Notify Users")} | ${t(
    process.env.REACT_APP_HEADING
  )}`;

  //#region notification
  const {
    sendingNotification,
    sendingNotificationSuccess,
    sendingNotificationError,
    notification,
  } = useSelector((state) => state.Notifications);

  /**
   * Formik handling
   */
  const notificationForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      audience: notification.audience || "all",
      title: notification.title || "",
      arabicTitle: notification.arabicTitle || "",
      body: notification.body || "",
      arabicBody: notification.arabicBody || "",
    },

    validationSchema: Yup.object({
      audience: Yup.string().required(t("Validations.Audience.Required")),
      title: Yup.string()
        .max(50, t("Validations.Common.Max", { length: 50 }))
        .required(t("Validations.Title.Required")),
      arabicTitle: Yup.string()
        .max(50, t("Validations.Common.Max", { length: 50 }))
        .required(t("Validations.ArabicTitle.Required")),
      body: Yup.string()
        .max(255, t("Validations.Common.Max", { length: 255 }))
        .required(t("Validations.Body.Required")),
      arabicBody: Yup.string()
        .max(255, t("Validations.Common.Max", { length: 255 }))
        .required(t("Validations.ArabicBody.Required")),
    }),

    onSubmit: (values) => {
      let roles = [];
      if (values.audience === "all") roles = ["Customer", "Technician"];
      if (values.audience === "customer") roles = ["Customer"];
      if (values.audience === "technician") roles = ["Technician"];
      values = { ...values, roles };
      dispatch(sendNotificationV2(values));
    },
  });

  useEffect(() => {
    if (sendingNotificationSuccess) {
      notificationForm.resetForm({
        audience: "all",
        title: "",
        arabicTitle: "",
        body: "",
        arabicBody: "",
      });
    }
  }, [sendingNotificationSuccess]);

  //#endregion

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("Notifications.Notify Users")}
            pageTitle={t("Common.Users")}
          />
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      notificationForm.handleSubmit();
                      return false;
                    }}
                    action="#"
                  >
                    <Row className="justify-content-center mb-3">
                      {sendingNotificationError && sendingNotificationError ? (
                        <Alert color="danger">
                          {" "}
                          {t(`Messages.Error.${sendingNotificationError}`)}{" "}
                        </Alert>
                      ) : null}
                      <div className="mb-3 col-lg-3">
                        <Label htmlFor="title" className="form-label">
                          {t("Fields.Audience")}*
                        </Label>
                        <select
                          id="audience"
                          value={notificationForm.values.audience || "all"}
                          onChange={notificationForm.handleChange}
                          onBlur={notificationForm.handleBlur}
                          className="form-select"
                          aria-label="Default select example"
                        >
                          <option value={"all"} key={"all"}>
                            {t("Common.All Users")}
                          </option>
                          <option value={"customer"} key={"customer"}>
                            {t("Customers.Customers")}
                          </option>
                          <option value={"technician"} key={"technician"}>
                            {t("Technicians.Technicians")}
                          </option>
                        </select>
                        {notificationForm.touched.audience &&
                        notificationForm.errors.audience ? (
                          <FormFeedback type="invalid">
                            {notificationForm.errors.audience}
                          </FormFeedback>
                        ) : null}
                      </div>
                      <div className="col-lg-3"></div>
                      <div className="clearfix"></div>

                      <div className="mb-3 col-lg-3">
                        <Label htmlFor="title" className="form-label">
                          {t("Fields.Title")}*
                        </Label>
                        <Input
                          type="text"
                          className="form-control"
                          id="title"
                          placeholder={t("Fields.Enter Title")}
                          value={notificationForm.values.title || ""}
                          onChange={notificationForm.handleChange}
                          onBlur={notificationForm.handleBlur}
                          invalid={
                            notificationForm.touched.title &&
                            notificationForm.errors.title
                              ? true
                              : false
                          }
                        />
                        {notificationForm.touched.title &&
                        notificationForm.errors.title ? (
                          <FormFeedback type="invalid">
                            {notificationForm.errors.title}
                          </FormFeedback>
                        ) : null}
                      </div>

                      <div className="mb-3 col-lg-3">
                        <Label htmlFor="arabicTitle" className="form-label">
                          {t("Fields.Arabic Title")}*
                        </Label>
                        <Input
                          type="text"
                          className="form-control"
                          id="arabicTitle"
                          placeholder={t("Fields.Enter Arabic Title")}
                          value={notificationForm.values.arabicTitle || ""}
                          onChange={notificationForm.handleChange}
                          onBlur={notificationForm.handleBlur}
                          invalid={
                            notificationForm.touched.arabicTitle &&
                            notificationForm.errors.arabicTitle
                              ? true
                              : false
                          }
                        />
                        {notificationForm.touched.arabicTitle &&
                        notificationForm.errors.arabicTitle ? (
                          <FormFeedback type="invalid">
                            {notificationForm.errors.arabicTitle}
                          </FormFeedback>
                        ) : null}
                      </div>

                      <div className="clearfix"></div>

                      <div className="mb-3 col-lg-6">
                        <Label htmlFor="body" className="form-label">
                          {t("Fields.Body")}*
                        </Label>
                        <Input
                          type="textarea"
                          row="2"
                          className="form-control"
                          id="body"
                          placeholder={t("Fields.Enter message body")}
                          value={notificationForm.values.body || ""}
                          onChange={notificationForm.handleChange}
                          onBlur={notificationForm.handleBlur}
                          invalid={
                            notificationForm.touched.body &&
                            notificationForm.errors.body
                              ? true
                              : false
                          }
                        />
                        {notificationForm.touched.body &&
                        notificationForm.errors.body ? (
                          <FormFeedback type="invalid">
                            {notificationForm.errors.body}
                          </FormFeedback>
                        ) : null}
                      </div>

                      <div className="clearfix"></div>

                      <div className="mb-3 col-lg-6">
                        <Label htmlFor="arabicBody" className="form-label">
                          {t("Fields.Arabic Body")}*
                        </Label>
                        <Input
                          type="textarea"
                          row="2"
                          className="form-control"
                          id="arabicBody"
                          placeholder={t("Fields.Enter message body in arabic")}
                          value={notificationForm.values.arabicBody || ""}
                          onChange={notificationForm.handleChange}
                          onBlur={notificationForm.handleBlur}
                          invalid={
                            notificationForm.touched.arabicBody &&
                            notificationForm.errors.arabicBody
                              ? true
                              : false
                          }
                        />
                        {notificationForm.touched.arabicBody &&
                        notificationForm.errors.arabicBody ? (
                          <FormFeedback type="invalid">
                            {notificationForm.errors.arabicBody}
                          </FormFeedback>
                        ) : null}
                      </div>
                      <div className="clearfix"></div>

                      <div className="col-lg-6">
                        <div className="hstack gap-2 justify-content-end">
                          <button
                            type="submit"
                            className="btn btn-success"
                            id="add-btn"
                            disabled={sendingNotification}
                          >
                            {sendingNotification ? (
                              <span className="d-flex align-items-center">
                                <span className="flex-grow-1 me-2">
                                  {t("Common.Sending")}...
                                </span>
                                <Spinner
                                  size="sm"
                                  className="flex-shrink-0"
                                  role="status"
                                >
                                  {" "}
                                  {t("Common.Sending")}
                                  ...{" "}
                                </Spinner>
                              </span>
                            ) : (
                              <>{t("Common.Send")}</>
                            )}
                          </button>
                        </div>
                      </div>
                    </Row>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default NotifyUsersPage;
